import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse, HttpInterceptorFn, HttpStatusCode } from '@angular/common/http';
import { PLATFORM_ID, inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { MessageService } from 'primeng/api';

import { AuthStore } from './auth.store';

const TOKEN_STORAGE_KEY = 'auth_token';

export const httpErrorInterceptor: HttpInterceptorFn = (request, next) => {
    const authStore = inject(AuthStore);
    const router = inject(Router);
    const platformId = inject(PLATFORM_ID);
    const messageService = inject(MessageService);

    return next(request).pipe(
        catchError((error: unknown) => {
            if (!(error instanceof HttpErrorResponse)) {
                return throwError(() => new Error('Erro inesperado de comunicação.'));
            }

            if (error.status === HttpStatusCode.Unauthorized) {
                authStore.clear();

                if (isPlatformBrowser(platformId)) {
                    localStorage.removeItem(TOKEN_STORAGE_KEY);
                }

                router.navigateByUrl('/auth/login');
            }

            const errorMessage = extractErrorMessage(error);

            messageService.add({
                severity: 'error',
                summary: 'Erro',
                detail: errorMessage
            });

            return throwError(() => error);
        })
    );
};

function extractErrorMessage(error: HttpErrorResponse): string {
    const payload = error.error;

    if (typeof payload === 'string' && payload.trim()) {
        return payload;
    }

    if (payload && typeof payload === 'object') {
        const validationMessages = extractValidationMessages(payload, error.status);

        if (validationMessages) {
            return validationMessages;
        }

        if ('message' in payload) {
            const message = (payload as { message?: unknown }).message;

            if (typeof message === 'string' && message.trim()) {
                return message;
            }
        }
    }

    if (error.message?.trim()) {
        return error.message;
    }

    return 'Falha na requisição.';
}

/**
 * Backend (ApiResponse::validationError) envia os erros de campo em `data`.
 * Laravel padrão usa `errors`. Ambos são tratados.
 */
function extractValidationMessages(payload: object, status: number): string | null {
    if (status !== HttpStatusCode.UnprocessableEntity && status !== HttpStatusCode.BadRequest) {
        return null;
    }

    const record = payload as { data?: unknown; errors?: unknown };
    const source = record.data ?? record.errors;

    if (!source) {
        return null;
    }

    if (Array.isArray(source)) {
        const messages = source.map(String).map((msg) => msg.trim()).filter(Boolean);
        return messages.length ? messages.join(' ') : null;
    }

    if (typeof source !== 'object') {
        return null;
    }

    const messages = Object.values(source as Record<string, unknown>)
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .map(String)
        .map((msg) => msg.trim())
        .filter(Boolean);

    return messages.length ? messages.join(' ') : null;
}
